import React from 'react';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { IntegrationSettings, OverrideFieldSettings } from '../../services/integrationApi';
import OverrideFieldsPicker from '../OverrideFieldsPicker';
import { SectionCard } from '../ui';

const T = 'integrations.lightspeedConfig.dimensions';

export interface LightspeedDimensionsSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
}

/**
 * Weight and size on Lightspeed items (flag `lightspeed_item_dimensions`): whether Motzu may replace
 * the weight and box on items already in Lightspeed (the shared `weight` override row), and the unit
 * system the Lightspeed account measures in.
 */
export const LightspeedDimensionsSection: React.FC<LightspeedDimensionsSectionProps> = ({ settings, onChange }) => {
  const { t } = useAppTranslation();
  if (settings.syncMode === 'link_only') return null;
  const units = settings.dimensionUnits === 'metric' ? 'metric' : 'imperial';
  return (
    <SectionCard title={t(`${T}.section`)} dense>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t(`${T}.units.label`)}
          </Typography>
          <RadioGroup
            row
            value={units}
            onChange={(e) => onChange({ dimensionUnits: e.target.value === 'metric' ? 'metric' : 'imperial' })}
          >
            <FormControlLabel value="imperial" control={<Radio size="small" />} label={t(`${T}.units.imperial`)} />
            <FormControlLabel value="metric" control={<Radio size="small" />} label={t(`${T}.units.metric`)} />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            {t(`${T}.units.help`)}
          </Typography>
        </Box>
        <OverrideFieldsPicker
          value={settings.overrideFields}
          onChange={(overrideFields: OverrideFieldSettings) => onChange({ overrideFields })}
          imagesSyncEnabled={settings.syncImages !== false}
          fields={['weight']}
          copy={{
            title: t(`${T}.heading`),
            subtitle: t(`${T}.subtitle`),
            keepNote: t(`${T}.note`),
            fields: { weight: { label: t(`${T}.weight.label`), desc: t(`${T}.weight.desc`) } },
          }}
        />
      </Box>
    </SectionCard>
  );
};
